import { useCallback, useEffect, useMemo, useState, Fragment } from "react"
import { useNavigate } from "react-router-dom"
import { openInNewTab } from "../../../utils/downloadFile"
import Table from "../../table"
import EngagementsCell from "./cells/engagements"
import MenuCell from "./cells/menu"
import OwnerCell from "./cells/owner"
import ViewDashboardCell from "./cells/view-dashboard"
import ManageEngagementPanel from "../manage"
import SubscriptionStats, { ChartClickData } from "../subscription-stats"
import FilterChips from "../../charts/filter-chips"
import {
  useFetchSubscriptionDetailsQuery,
  useFetchSubscriptionLogsQuery
} from "../../../../redux/queries"
import { useAppDispatch, useAppSelector } from "../../../hooks"
import { selectUserDetails } from "../../../../redux/slices/auth"
import {
  selectTabRange,
  updateRange
} from "../../../../redux/slices/engagements"
import {
  checkIsAdmin,
  checkIsSuperAdmin,
  copyToClipboard,
  dateFilterToTimestamps
} from "../../../utils"
import { SubscriptionLog, BBColumnDef, EngagementsTab } from "../../../../types"
import DateFilter, { DateRangePreset } from "../../activity/date-filter"
import { FilterFnOption } from "@tanstack/react-table"

const EngagementsTable = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const userDetails = useAppSelector(selectUserDetails)

  const isAdmin = useMemo(
    () => checkIsAdmin(userDetails?.roles ?? []),
    [userDetails?.roles]
  )
  const isSuperAdmin = useMemo(
    () => checkIsSuperAdmin(userDetails?.roles ?? []),
    [userDetails?.roles]
  )

  const loid = userDetails?.id
  const orgId = userDetails?.org?.id
  const [selectedSubscriptionId, setSelectedSubscriptionId] =
    useState<string>("")
  const [chartFilter, setChartFilter] = useState<ChartClickData | null>(null)

  const {
    data: selectedSubscription,
    isFetching: isFetchingSelectedSubscription
  } = useFetchSubscriptionDetailsQuery(
    { id: selectedSubscriptionId },
    { skip: !selectedSubscriptionId }
  )

  // Keep panel closed during refetch to prevent it from reopening
  // Only close if we're refetching AND we already have data (not initial load)
  useEffect(() => {
    if (
      isFetchingSelectedSubscription &&
      selectedSubscriptionId &&
      selectedSubscription
    ) {
      // During refetch after update, ensure panel stays closed
      setSelectedSubscriptionId("")
    }
  }, [
    isFetchingSelectedSubscription,
    selectedSubscriptionId,
    selectedSubscription
  ])

  const openLink = useCallback((link: string) => {
    if (link) {
      openInNewTab(link)
    }
  }, [])

  const navigateToDetails = useCallback(
    (id: string) => navigate(`/engagements/${id}`),
    [navigate]
  )

  const resetSelectedSubscriptionId = useCallback(
    () => setSelectedSubscriptionId(""),
    []
  )

  const columns: BBColumnDef<SubscriptionLog>[] = useMemo(
    () => [
      {
        id: "engagements",
        header: "Subscriptions",
        accessorFn: row => `${row.first_name} ${row.last_name} ${row.email}`,
        footer: props => props.column.id,
        cell: props => (
          <EngagementsCell
            info={props.row.original}
            onClick={navigateToDetails}
          />
        ),
        colSpan: 2,
        filterFn:
          "rowStatusFilter" as unknown as FilterFnOption<SubscriptionLog>
      },
      {
        id: "owner",
        header: "Owner",
        accessorFn: row => row.owner_email,
        footer: props => props.column.id,
        cell: props => (
          <OwnerCell info={props.row.original} onClick={navigateToDetails} />
        ),
        meta: { headerClassName: "hidden md:table-cell" }
      },
      {
        id: "view-dashboard",
        header: "",
        accessorFn: row => row.id,
        footer: props => props.column.id,
        cell: props => <ViewDashboardCell info={props.row.original} />,
        meta: { headerClassName: "hidden md:table-cell" },
        enableSorting: false
      },
      {
        id: "created",
        header: "",
        accessorFn: row => row.created,
        footer: props => props.column.id,
        cell: props => "",
        colSpan: 0,
        meta: { headerClassName: "hidden" }
      },
      {
        id: "edit",
        header: "",
        footer: props => props.column.id,
        enableSorting: false,
        cell: props => (
          <MenuCell
            info={props.row.original}
            onCopyDashboardLink={copyToClipboard}
            onEdit={setSelectedSubscriptionId}
            onViewLeadDashboard={openLink}
          />
        )
      }
    ],
    [navigateToDetails, openLink]
  )

  const tabKey = EngagementsTab.engagements
  const dateFilter = useAppSelector(state => selectTabRange(state, tabKey))

  const handleDateRangeChange = useCallback(
    (range: DateRangePreset, customStartDate?: Date, customEndDate?: Date) => {
      dispatch(
        updateRange({
          tabKey,
          range,
          customStartDate: customStartDate || null,
          customEndDate: customEndDate || null
        })
      )
      // Clear chart filter when manually changing date range
      setChartFilter(null)
    },
    [dispatch, tabKey]
  )

  const handleClearDateFilter = useCallback(() => {
    dispatch(
      updateRange({
        tabKey,
        range: "all",
        customStartDate: null,
        customEndDate: null
      })
    )
  }, [dispatch, tabKey])

  // Handle chart click to update date filter
  const handleChartClick = useCallback(
    (data: ChartClickData) => {
      setChartFilter(data)

      // Convert chart period to date range
      if (data.viewType === "daily") {
        // Parse ISO date string (e.g., "2025-10-29")
        const date = new Date(data.period)
        dispatch(
          updateRange({
            tabKey,
            range: "custom",
            customStartDate: date,
            customEndDate: date
          })
        )
      } else {
        // Monthly view: "2025-January"
        const [year, monthName] = data.period.split("-")
        const monthMap: Record<string, number> = {
          January: 0,
          February: 1,
          March: 2,
          April: 3,
          May: 4,
          June: 5,
          July: 6,
          August: 7,
          September: 8,
          October: 9,
          November: 10,
          December: 11
        }
        const month = monthMap[monthName]

        const startOfMonth = new Date(parseInt(year), month, 1)
        const endOfMonth = new Date(parseInt(year), month + 1, 0)

        dispatch(
          updateRange({
            tabKey,
            range: "custom",
            customStartDate: startOfMonth,
            customEndDate: endOfMonth
          })
        )
      }
    },
    [dispatch, tabKey]
  )

  const handleClearChartFilter = useCallback(() => {
    setChartFilter(null)
  }, [])

  // Calculate timestamps from date range
  const { min: timestamp_min, max: timestamp_max } = useMemo(
    () => dateFilterToTimestamps(dateFilter),
    [dateFilter]
  )

  // subscription logs
  const { data: subscriptionLogs, isFetching } = useFetchSubscriptionLogsQuery({
    limit: 99999,
    offset: 0,
    ...(timestamp_min !== undefined && timestamp_max !== undefined
      ? { min_created: timestamp_min, max_created: timestamp_max }
      : {}),
    ...(isSuperAdmin ? {} : isAdmin ? { org_id: orgId } : { loid })
  })

  return (
    <div>
      <Table<SubscriptionLog>
        className="engagements-table"
        isLoading={isFetching}
        data={subscriptionLogs || []}
        columns={columns}
        viewType="table"
        dropdownFilterColumnId="engagements"
        searchPlaceholder="Search subscriptions…"
        extraHeaderElement={
          <DateFilter
            selectedRange={dateFilter.range}
            customStartDate={dateFilter.customStartDate}
            customEndDate={dateFilter.customEndDate}
            onRangeChange={handleDateRangeChange}
            onClearFilter={handleClearDateFilter}
          />
        }
        extraHeaderElementClassName="ml-auto"
        shrinkFilterSort
        defaultSortBy="created"
        defaultSortDir="desc"
        extraElementBeforeTable={
          <Fragment>
            <SubscriptionStats
              orgId={isSuperAdmin ? undefined : orgId}
              loid={isSuperAdmin || isAdmin ? undefined : loid}
              onChartClick={handleChartClick}
              selectedBarIndex={chartFilter?.index ?? null}
            />
            {chartFilter && (
              <FilterChips
                filter={chartFilter}
                onClear={handleClearChartFilter}
                currentCount={subscriptionLogs?.length || 0}
                totalCount={subscriptionLogs?.length || 0}
                itemLabel="subscriptions"
              />
            )}
          </Fragment>
        }
        hasColumnFilter={false}
        hasActiveDeletedFilter
      />

      <ManageEngagementPanel
        data={selectedSubscription}
        open={!!(selectedSubscriptionId && selectedSubscription)}
        onClose={resetSelectedSubscriptionId}
      />
    </div>
  )
}

export default EngagementsTable
